/**
 * useLoginForm
 *
 * All form logic for the login flow.
 * Extension point: add MFA, remember-me, or social auth here without touching LoginPage.
 */

import { useState, useCallback, type ChangeEvent, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { ApiError } from '../api/client';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface LoginFields {
  email: string;
  password: string;
}

export interface LoginFieldErrors {
  email?: string;
  password?: string;
}

export interface UseLoginFormReturn {
  fields: LoginFields;
  errors: LoginFieldErrors;
  serverError: string | null;
  loading: boolean;
  touched: Record<keyof LoginFields, boolean>;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: FormEvent) => void;
}

// ── Validation ────────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(fields: LoginFields): LoginFieldErrors {
  const errs: LoginFieldErrors = {};

  if (!fields.email) {
    errs.email = 'Email is required';
  } else if (!EMAIL_RE.test(fields.email)) {
    errs.email = 'Enter a valid email address';
  }

  if (!fields.password) {
    errs.password = 'Password is required';
  }

  return errs;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

const INITIAL_FIELDS: LoginFields = { email: '', password: '' };
const INITIAL_TOUCHED: Record<keyof LoginFields, boolean> = {
  email: false,
  password: false,
};

export function useLoginForm(): UseLoginFormReturn {
  const { login } = useAuth();
  const location = useLocation();
  const from =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/practice';

  const [fields, setFields] = useState<LoginFields>(INITIAL_FIELDS);
  const [touched, setTouched] = useState(INITIAL_TOUCHED);
  const [serverError, setServerError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const allErrors = validate(fields);

  const errors: LoginFieldErrors = {
    email:    touched.email    ? allErrors.email    : undefined,
    password: touched.password ? allErrors.password : undefined,
  };

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
    setServerError(null);
  }, []);

  const handleBlur = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  }, []);

  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();

    setTouched({ email: true, password: true });
    const errs = validate(fields);
    if (Object.keys(errs).length > 0) return;

    setServerError(null);
    setLoading(true);

    login({ email: fields.email, password: fields.password }, from)
      .catch((err: unknown) => {
        if (err instanceof ApiError) {
          if (err.status === 401) {
            // Intentionally vague — no enumeration attack surface
            setServerError('Invalid email or password');
          } else if (err.status === 403) {
            // EMAIL_NOT_VERIFIED gate
            setServerError(
              'Email not verified. Check your inbox or request a new link.',
            );
          } else if (err.status === 429) {
            setServerError('Too many attempts — account temporarily locked');
          } else {
            setServerError('Sign in failed — please try again');
          }
        } else {
          setServerError('Network error — check your connection');
        }
      })
      .finally(() => setLoading(false));
  }, [fields, login, from]);

  return { fields, errors, serverError, loading, touched, handleChange, handleBlur, handleSubmit };
}
